import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { adminPosts, postTypeLabels } from '@/mocks/adminPosts';
import type { PostType, PostStatus, AdminPost, PollData } from '@/mocks/adminPosts';
import FeedPostCard from './components/FeedPostCard';
import CreatePostComposer from './components/CreatePostComposer';
import InsightsPanel from './components/InsightsPanel';
import PostCard from './components/PostCard';
import PostFormModal from './components/PostFormModal';
import PollResultsModal from './components/PollResultsModal';
import type { PostFormData } from './components/PostFormModal';
import NotificationFlowModal from '@/pages/admin/notifications/components/NotificationFlowModal';
import type { NotificationFlowContext } from '@/pages/admin/notifications/components/NotificationFlowModal';

const postTypes: PostType[] = ['texto', 'imagen', 'video', 'encuesta', 'patrocinada'];
const postStatuses: PostStatus[] = ['publicado', 'borrador', 'programado'];

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

export default function AdminPosts() {
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<PostType | 'todos'>('todos');
  const [filterStatus, setFilterStatus] = useState<PostStatus | 'todos' | 'destacadas'>('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<AdminPost | null>(null);
  const [defaultType, setDefaultType] = useState<PostType | undefined>(undefined);
  const [pageState, setPageState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [resultsPost, setResultsPost] = useState<AdminPost | null>(null);
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'promocion', relatedModule: 'posts' },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.05;
      if (shouldFail) {
        setPageState('error');
      } else {
        setPosts(adminPosts);
        setPageState('ready');
      }
    }, 800 + Math.random() * 700);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.03;
      if (shouldFail) {
        setPageState('error');
      } else {
        setPosts(adminPosts);
        setPageState('ready');
      }
    }, 600 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  const filteredPosts = useMemo(() => {
    let result = posts;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((p) =>
        p.title.toLowerCase().includes(q) || p.text.toLowerCase().includes(q)
      );
    }

    if (filterType !== 'todos') {
      result = result.filter((p) => p.type === filterType);
    }

    if (filterStatus === 'destacadas') {
      result = result.filter((p) => p.isFeatured);
    } else if (filterStatus !== 'todos') {
      result = result.filter((p) => p.status === filterStatus);
    }

    return result;
  }, [posts, search, filterType, filterStatus]);

  const handleCreate = (type?: PostType) => {
    setEditingPost(null);
    setDefaultType(type);
    setModalOpen(true);
  };

  const handleEdit = (post: AdminPost) => {
    setEditingPost(post);
    setDefaultType(undefined);
    setModalOpen(true);
  };

  const handleSave = (data: PostFormData) => {
    const isNew = !editingPost;

    const pollData: PollData | undefined = data.type === 'encuesta'
      ? {
          pollType: data.pollType,
          options: data.pollOptions.map((opt, idx) => ({
            id: editingPost?.pollData?.options[idx]?.id || idx + 1,
            text: opt.text,
            votes: editingPost?.pollData?.options[idx]?.votes || 0,
          })),
          isClosed: editingPost?.pollData?.isClosed || false,
        }
      : undefined;

    if (isNew) {
      const newPost: AdminPost = {
        id: Math.max(...posts.map((p) => p.id), 0) + 1,
        title: data.title,
        text: data.text,
        type: data.type,
        imageUrl: data.imageUrl || undefined,
        videoUrl: data.videoUrl || undefined,
        scheduledDate: data.scheduledDate || undefined,
        status: data.status,
        isFeatured: data.isFeatured,
        isSponsored: data.isSponsored,
        sponsorName: data.sponsorName || undefined,
        engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
        pollData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setPosts((prev) => [newPost, ...prev]);
    } else {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === editingPost.id
            ? {
                ...p,
                title: data.title,
                text: data.text,
                type: data.type,
                imageUrl: data.imageUrl || undefined,
                videoUrl: data.videoUrl || undefined,
                scheduledDate: data.scheduledDate || undefined,
                status: data.status,
                isFeatured: data.isFeatured,
                isSponsored: data.isSponsored,
                sponsorName: data.sponsorName || undefined,
                pollData,
                updatedAt: new Date().toISOString(),
              }
            : p
        )
      );
    }

    setModalOpen(false);
    setEditingPost(null);
    setDefaultType(undefined);

    const toastMessage = isNew ? 'Publicación creada exitosamente.' : 'Publicación actualizada exitosamente.';
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: toastMessage,
      icon: 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  const handleDelete = (post: AdminPost) => {
    sharedSwal
      .fire({
        title: '¿Eliminar publicación?',
        html: `<p class="text-sm text-foreground-500">Esta acción no se puede deshacer. Se eliminará permanentemente <strong>&ldquo;${escapeHtml(post.title.slice(0, 60))}&rdquo;</strong>.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setPosts((prev) => prev.filter((p) => p.id !== post.id));
          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Publicación eliminada.',
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
  };

  const handlePublishNow = (post: AdminPost) => {
    sharedSwal
      .fire({
        title: '¿Publicar ahora?',
        html: `<p class="text-sm text-foreground-500">La publicación <strong>&ldquo;${escapeHtml(post.title.slice(0, 60))}&rdquo;</strong> se publicará inmediatamente.</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Publicar ahora',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === post.id
                ? { ...p, status: 'publicado' as PostStatus, scheduledDate: undefined, updatedAt: new Date().toISOString() }
                : p
            )
          );
          Swal.fire({
            toast: true,
            position: 'top-end',
            title: '¡Publicación publicada!',
            icon: 'success',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });

          // Offer to notify
          Swal.fire({
            title: '¿Notificar a tus oyentes?',
            text: `¿Querés enviar una notificación push sobre "${post.title.slice(0, 60)}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Notificar',
            cancelButtonText: 'Ahora no',
            confirmButtonColor: '#0ea5e9',
            cancelButtonColor: '#9ca3af',
            customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
            buttonsStyling: false,
          }).then((notifyResult) => {
            if (notifyResult.isConfirmed) {
              setFlowModal({
                open: true,
                context: {
                  type: 'promocion',
                  relatedModule: 'posts',
                  relatedItemId: post.id,
                  relatedItemName: post.title,
                  prefilledTitle: `Nueva publicación: ${post.title.slice(0, 50)}`,
                  prefilledMessage: `Acabamos de publicar "${post.title.slice(0, 80)}". ${post.text.slice(0, 100)}... ¡Leelo ahora!`,
                },
              });
            }
          });
        }
      });
  };

  const handleToggleFeatured = (post: AdminPost) => {
    const newFeatured = !post.isFeatured;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id ? { ...p, isFeatured: newFeatured, updatedAt: new Date().toISOString() } : p
      )
    );
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: newFeatured ? 'Marcada como destacada.' : 'Quitada de destacadas.',
      icon: 'success',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  const handleShare = (post: AdminPost) => {
    const shareLink = `https://radionuevaactitud.com/publicaciones/${post.id}`;
    sharedSwal
      .fire({
        title: 'Compartir publicación',
        html: `
          <div class="space-y-4">
            <p class="text-sm text-foreground-500 mb-3">Compartí <strong>&ldquo;${escapeHtml(post.title.slice(0, 60))}&rdquo;</strong> con tu audiencia.</p>
            <div class="flex items-center gap-2 bg-background-100 rounded-xl p-2.5 mb-4">
              <input type="text" value="${shareLink}" readonly class="flex-1 bg-transparent text-xs text-foreground-700 outline-none border-none" id="share-link-input" />
              <button type="button" class="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-[11px] font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap" id="copy-share-link">Copiar</button>
            </div>
            <div class="flex items-center gap-2 justify-center">
              <button type="button" class="share-btn w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-colors" data-platform="facebook"><i class="ri-facebook-fill text-white text-lg"></i></button>
              <button type="button" class="share-btn w-10 h-10 rounded-full bg-sky-400 flex items-center justify-center cursor-pointer hover:bg-sky-500 transition-colors" data-platform="twitter"><i class="ri-twitter-x-fill text-white text-lg"></i></button>
              <button type="button" class="share-btn w-10 h-10 rounded-full bg-green-500 flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" data-platform="whatsapp"><i class="ri-whatsapp-line text-white text-lg"></i></button>
              <button type="button" class="share-btn w-10 h-10 rounded-full bg-foreground-700 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors" data-platform="copy"><i class="ri-link text-white text-lg"></i></button>
            </div>
          </div>
        `,
        icon: undefined,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
        customClass: { popup: 'rounded-2xl', title: 'font-bold' },
        buttonsStyling: false,
        didOpen: () => {
          const copyBtn = document.getElementById('copy-share-link');
          const input = document.getElementById('share-link-input') as HTMLInputElement;
          copyBtn?.addEventListener('click', () => {
            if (input) {
              navigator.clipboard.writeText(input.value).then(() => {
                copyBtn.textContent = '¡Copiado!';
                copyBtn.classList.add('bg-emerald-500');
                setTimeout(() => { copyBtn.textContent = 'Copiar'; copyBtn.classList.remove('bg-emerald-500'); }, 2000);
              });
            }
          });
          document.querySelectorAll('.share-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
              const platform = (btn as HTMLElement).dataset.platform;
              if (platform === 'copy' && input) navigator.clipboard.writeText(input.value);
              Swal.clickConfirm();
              Swal.fire({
                toast: true, position: 'top-end',
                title: platform === 'copy' ? 'Link copiado.' : `Compartido en ${platform === 'facebook' ? 'Facebook' : platform === 'twitter' ? 'X' : 'WhatsApp'}.`,
                icon: 'success', showConfirmButton: false, timer: 2000, timerProgressBar: true,
                customClass: { popup: 'rounded-xl' },
              });
            });
          });
        },
      })
      .then(() => {
        setPosts((prev) =>
          prev.map((p) =>
            p.id === post.id ? { ...p, engagement: { ...p.engagement, shares: p.engagement.shares + 1 } } : p
          )
        );
      });
  };

  const handleViewResults = (post: AdminPost) => {
    setResultsPost(post);
  };

  const handleClosePoll = (post: AdminPost) => {
    if (!post.pollData) return;
    const isCurrentlyClosed = post.pollData.isClosed;

    sharedSwal
      .fire({
        title: isCurrentlyClosed ? '¿Reabrir encuesta?' : '¿Cerrar encuesta?',
        html: isCurrentlyClosed
          ? `<p class="text-sm text-foreground-500">La encuesta <strong>&ldquo;${escapeHtml(post.title.slice(0, 60))}&rdquo;</strong> se reabrirá y volverá a aceptar votos.</p>`
          : `<p class="text-sm text-foreground-500">La encuesta <strong>&ldquo;${escapeHtml(post.title.slice(0, 60))}&rdquo;</strong> se cerrará. Ya no se podrá votar pero los resultados seguirán visibles.</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: isCurrentlyClosed ? 'Reabrir encuesta' : 'Cerrar encuesta',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: isCurrentlyClosed ? '#10b981' : '#f59e0b',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === post.id && p.pollData
                ? { ...p, pollData: { ...p.pollData, isClosed: !isCurrentlyClosed }, updatedAt: new Date().toISOString() }
                : p
            )
          );
          Swal.fire({
            toast: true, position: 'top-end',
            title: isCurrentlyClosed ? 'Encuesta reabierta.' : 'Encuesta cerrada.',
            icon: 'success', showConfirmButton: false, timer: 2000, timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
  };

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: posts.length };
    postTypes.forEach((t) => { counts[t] = posts.filter((p) => p.type === t).length; });
    postStatuses.forEach((s) => { counts[s] = posts.filter((p) => p.status === s).length; });
    counts['destacadas'] = posts.filter((p) => p.isFeatured).length;
    return counts;
  }, [posts]);

  // ── Loading ──
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-40 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 space-y-5 max-w-[1100px] mx-auto">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <div className="w-full h-10 rounded-xl bg-background-200 animate-pulse mb-4"></div>
                <div className="flex items-center gap-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-16 h-7 rounded-full bg-background-200 animate-pulse"></div>
                  ))}
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-1 max-w-[680px] mx-auto space-y-4">
                  <div className="bg-background-50 rounded-xl border border-background-200 h-28 animate-pulse"></div>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-background-50 rounded-xl border border-background-200 h-64 animate-pulse"></div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">Error cargando publicaciones</h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">No se pudieron cargar las publicaciones. Verificá tu conexión e intentá de nuevo.</p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Ready ──
  return (
    <div className="min-h-screen bg-background-50">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Publicaciones</h1>
              <p className="text-foreground-400 text-[9px]">{posts.length} publicaciones</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>


          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Publicaciones</h2>
              <p className="text-foreground-400 text-xs">Gestioná el contenido de tu radio. {posts.length} publicaciones en total.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <button
                onClick={() => handleCreate()}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Crear publicación
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[1100px] mx-auto">
              {/* Filters bar */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative flex-1">
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar publicaciones..."
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                    />
                  </div>
                  <button
                    onClick={() => handleCreate()}
                    className="lg:hidden flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line text-base"></i>
                    Crear
                  </button>
                </div>

                {/* Type filter chips */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setFilterType('todos')}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      filterType === 'todos' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    Todos ({typeCounts.todos})
                  </button>
                  {postTypes.map((t) => (
                    <button
                      key={t}
                      onClick={() => setFilterType(t)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                        filterType === t ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {postTypeLabels[t]} ({typeCounts[t] || 0})
                    </button>
                  ))}
                </div>

                {/* Status filter pills */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => setFilterStatus('todos')}
                    className={`px-3 py-1 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      filterStatus === 'todos' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                    }`}
                  >
                    Todos estados
                  </button>
                  {postStatuses.map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilterStatus(s)}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                        filterStatus === s ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                      }`}
                    >
                      {s === 'publicado' ? 'Publicadas' : s === 'borrador' ? 'Borradores' : 'Programadas'} ({typeCounts[s] || 0})
                    </button>
                  ))}
                  <button
                    onClick={() => setFilterStatus('destacadas')}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      filterStatus === 'destacadas' ? 'bg-amber-500 text-background-50' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                    }`}
                  >
                    <i className="ri-star-fill text-[10px]"></i>
                    Destacadas ({typeCounts['destacadas'] || 0})
                  </button>
                </div>
              </div>

              {/* Main layout: Feed + Insights */}
              <div className="flex gap-6">
                {/* Feed column */}
                <div className="flex-1 min-w-0 max-w-[680px] mx-auto space-y-4">
                  {/* Results count */}
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-foreground-400">
                      Mostrando {filteredPosts.length} de {posts.length} publicaciones
                    </p>
                    {(filterType !== 'todos' || filterStatus !== 'todos' || search) && (
                      <button
                        onClick={() => { setFilterType('todos'); setFilterStatus('todos'); setSearch(''); }}
                        className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </div>

                  {/* Create Post Composer */}
                  <CreatePostComposer onCreatePost={handleCreate} />

                  {/* Empty state */}
                  {filteredPosts.length === 0 ? (
                    <div className="bg-background-50 rounded-xl border border-background-200 p-12 text-center">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                        <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                      </div>
                      <h3 className="text-base font-bold text-foreground-700 mb-1">
                        {posts.length === 0 ? 'No hay publicaciones todavía' : 'Sin resultados'}
                      </h3>
                      <p className="text-sm text-foreground-400 mb-5 max-w-xs mx-auto leading-relaxed">
                        {posts.length === 0
                          ? 'Creá tu primera publicación para empezar a compartir contenido con tus oyentes.'
                          : 'No hay publicaciones que coincidan con los filtros seleccionados.'}
                      </p>
                      {posts.length === 0 ? (
                        <button
                          onClick={() => handleCreate()}
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-add-line text-base"></i>
                          Crear publicación
                        </button>
                      ) : (
                        <button
                          onClick={() => { setFilterType('todos'); setFilterStatus('todos'); setSearch(''); }}
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-sm font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-line text-base"></i>
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Desktop Feed */}
                      <div className="hidden lg:block space-y-4">
                        {filteredPosts.map((post) => (
                          <FeedPostCard
                            key={post.id}
                            post={post}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onToggleFeatured={handleToggleFeatured}
                            onPublishNow={handlePublishNow}
                            onShare={handleShare}
                            onViewResults={handleViewResults}
                            onClosePoll={handleClosePoll}
                          />
                        ))}
                      </div>

                      {/* Mobile Cards */}
                      <div className="lg:hidden space-y-3">
                        {filteredPosts.map((post) => (
                          <PostCard
                            key={post.id}
                            post={post}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onToggleFeatured={handleToggleFeatured}
                            onPublishNow={handlePublishNow}
                            onShare={handleShare}
                            onViewResults={handleViewResults}
                            onClosePoll={handleClosePoll}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Insights Panel (right, xl+) */}
                <InsightsPanel posts={posts} />
              </div>
            </div>
          </div>




      {/* Modals */}
      <PostFormModal
        open={modalOpen}
        post={editingPost}
        defaultType={defaultType}
        onClose={() => { setModalOpen(false); setEditingPost(null); setDefaultType(undefined); }}
        onSave={handleSave}
      />
      <PollResultsModal open={!!resultsPost} post={resultsPost} onClose={() => setResultsPost(null)} />
      <NotificationFlowModal open={flowModal.open} context={flowModal.context} onClose={() => setFlowModal((prev) => ({ ...prev, open: false }))} />
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}